<template>
  <div class="inline-form">
    <div class="inline-form-title">Cadastrar profissional</div>
    <div class="columns">
      <div
        class="column col-3 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.identity.value.$error }"
      >
        <label class="form-label">CPF</label>
        <div class="input-group">
          <input
            ref="cpf"
            type="text"
            class="form-input"
            v-model="form.identity.value"
            placeholder="000.000.000-00"
            autocomplete="nope"
            v-mask-cpf
            @blur="$v.form.identity.value.$touch()"
            @change="lookupByCpf"
          >
          <span class="input-group-addon" v-if="loadingCpf">
            <fa-icon :icon="['fal', 'spinner-third']" spin/>
          </span>
        </div>
        <template v-if="$v.form.identity.value.$error">
          <div class="form-input-hint" v-if="!$v.form.identity.value.required">
            Campo obrigatório
          </div>
          <div class="form-input-hint" v-else-if="!$v.form.identity.value.cpf">
            CPF inválido
          </div>
        </template>
      </div>
      <div
        class="column col-5 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.name.$error }"
      >
        <label class="form-label">Nome completo</label>
        <input
          ref="name"
          type="text"
          class="form-input"
          v-model="form.name"
          placeholder="Nome completo"
          autocomplete="nope"
          @blur="$v.form.name.$touch()"
        >
        <div class="form-input-hint" v-if="$v.form.name.$error && !$v.form.name.required">
          Campo obrigatório
        </div>
      </div>
      <div
        class="column col-2 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.birthDate.$error }"
      >
        <label class="form-label">Nascimento</label>
        <input
          type="text"
          class="form-input"
          v-model="form.birthDate"
          placeholder="dd/mm/aaaa"
          v-mask-date.br
          @blur="$v.form.birthDate.$touch()"
        >
        <template v-if="$v.form.birthDate.$error">
          <div class="form-input-hint" v-if="!$v.form.birthDate.required">Campo obrigatório</div>
          <div class="form-input-hint" v-else-if="!$v.form.birthDate.date">Data inválida</div>
        </template>
      </div>
      <div
        class="column col-2 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.gender.$error }"
      >
        <label class="form-label">Sexo</label>
        <select class="form-select" v-model="form.gender" @change="$v.form.gender.$touch()">
          <option value="">[Selecione]</option>
          <option value="female">Feminino</option>
          <option value="male">Masculino</option>
        </select>
        <div class="form-input-hint" v-if="$v.form.gender.$error && !$v.form.gender.required">
          Campo obrigatório
        </div>
      </div>
      <div class="column col-12" v-if="existingRole === 'professional'">
        <div class="form-input-hint text-info">
          Profissional já cadastrado. Os dados foram preenchidos automaticamente e não serão
          alterados; ele será apenas adicionado a este horário.
        </div>
      </div>
      <div class="column col-12" v-else-if="existingRole === 'collaborator'">
        <div class="form-input-hint text-info">
          Colaborador já cadastrado. Ele será adicionado como profissional, mantendo a senha
          e as permissões de acesso atuais.
        </div>
      </div>
    </div>

    <div class="columns">
      <div
        class="column col-3 col-md-6 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.professional.council.name.$error }"
      >
        <label class="form-label">Conselho</label>
        <select
          class="form-select"
          v-model="form.professional.council.name"
          @change="$v.form.professional.council.name.$touch()"
        >
          <option value="">[Selecione]</option>
          <option v-for="(label, key) in councils" :value="key" :key="key">{{ key }}</option>
        </select>
        <div
          class="form-input-hint"
          v-if="$v.form.professional.council.name.$error
            && !$v.form.professional.council.name.required"
        >Campo obrigatório</div>
      </div>
      <div
        class="column col-2 col-md-6 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.professional.council.state.$error }"
      >
        <label class="form-label">UF</label>
        <select
          class="form-select"
          v-model="form.professional.council.state"
          @change="$v.form.professional.council.state.$touch()"
        >
          <option value="">[Selecione]</option>
          <option v-for="(label, key) in states" :value="key" :key="key">{{ key }}</option>
        </select>
        <div
          class="form-input-hint"
          v-if="$v.form.professional.council.state.$error
            && !$v.form.professional.council.state.required"
        >Campo obrigatório</div>
      </div>
      <div
        class="column col-2 col-md-6 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.professional.council.record.$error }"
      >
        <label class="form-label">Registro</label>
        <input
          type="text"
          class="form-input"
          v-model="form.professional.council.record"
          placeholder="00000"
          autocomplete="nope"
          v-mask="'00000000'"
          @blur="$v.form.professional.council.record.$touch()"
        >
        <div
          class="form-input-hint"
          v-if="$v.form.professional.council.record.$error
            && !$v.form.professional.council.record.required"
        >Campo obrigatório</div>
      </div>
      <div
        class="column col-5 col-md-6 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.professional.specialties.$error }"
      >
        <label class="form-label">Especialidades</label>
        <select
          class="form-select"
          v-model="selectedSpecialty"
          :disabled="loadingSpecialties"
          @change="addSpecialty"
        >
          <option value="">{{ loadingSpecialties ? 'Carregando...' : '[Adicionar]' }}</option>
          <option
            v-for="item in specialtiesAvailable"
            :value="item.code"
            :key="item.code"
          >{{ item.name }}</option>
        </select>
        <div class="inline-chips" v-if="form.professional.specialties.length > 0">
          <span class="chip" v-for="(item, i) in form.professional.specialties" :key="item.code">
            {{ item.name }}
            <a href="#" class="btn btn-clear" aria-label="Remover"
               @click.prevent="removeSpecialty(i)"/>
          </span>
        </div>
        <div
          class="form-input-hint"
          v-if="$v.form.professional.specialties.$error
            && !$v.form.professional.specialties.required"
        >Campo obrigatório</div>
      </div>
    </div>

    <div class="inline-note" v-if="!existingRole">
      <fa-icon :icon="['fal', 'info-circle']"/>
      <span>
        O acesso ao sistema será criado automaticamente: o usuário é o
        <strong>CPF</strong> e a senha inicial é o <strong>CPF seguido de @</strong>,
        no grupo “Profissional da saúde (Padrão)”.
      </span>
    </div>

    <div class="inline-form-actions">
      <button class="btn btn-gray" :disabled="saving" @click="$emit('cancel')">Fechar</button>
      <button
        class="btn btn-secondary btn-icon btn-icon-left"
        :class="{ loading: saving }"
        :disabled="saving"
        @click="save"
      ><fa-icon :icon="['fal', 'save']"/>Adicionar</button>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import moment from 'moment/moment';
import { date, cpf } from '@/data/validators';
import { councils } from '@/data/council-types';
import states from '@/data/states';

const DEFAULT_PERMISSION_GROUP_ID = '60de4251040fd32028126424';

export default {
  props: {
    initialName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      specialties: [],
      selectedSpecialty: '',
      loadingSpecialties: false,
      loadingCpf: false,
      cpfAlreadyRegistered: false,
      existingRole: null,
      foundProfessionalId: null,
      hasLogin: false,
      cpfLookup: null,
      lookedUpCpf: '',
      saving: false,
      councils,
      states,
      form: this.blankForm(),
    };
  },
  mounted() {
    this.form.name = this.initialName;
    this.loadSpecialties();
    this.$nextTick(() => {
      if (this.$refs.cpf) this.$refs.cpf.focus();
    });
  },
  computed: {
    specialtiesAvailable() {
      return this.specialties.filter(item => (
        this.form.professional.specialties.every(s => s.code !== item.code)
      ));
    },
  },
  validations() {
    return {
      form: {
        identity: {
          value: { required, cpf },
        },
        name: { required },
        birthDate: { required, date },
        gender: { required },
        professional: {
          specialties: { required: v => v.length > 0 },
          council: {
            name: { required },
            state: { required },
            record: { required },
          },
        },
      },
    };
  },
  methods: {
    blankForm() {
      return {
        identity: { type: 'cpf', value: '' },
        name: '',
        birthDate: '',
        gender: '',
        professional: {
          council: { name: '', state: '', record: '' },
          specialties: [],
        },
      };
    },
    loadSpecialties() {
      this.loadingSpecialties = true;
      return this.$httpX
        .get('/services/cbos', { params: { limit: 0, isSpecialty: true } })
        .then(({ data }) => {
          this.specialties = data.items;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingSpecialties = false;
        });
    },
    addSpecialty() {
      const specialty = this.specialties.find(s => s.code === this.selectedSpecialty);
      if (specialty) {
        this.form.professional.specialties.push({ code: specialty.code, name: specialty.name });
        this.$v.form.professional.specialties.$touch();
      }
      this.selectedSpecialty = '';
    },
    removeSpecialty(i) {
      this.form.professional.specialties.splice(i, 1);
    },
    lookupByCpf() {
      this.$v.form.identity.value.$touch();
      const cpfNumbers = this.form.identity.value.replace(/\D/g, '');
      if (cpfNumbers.length !== 11 || !cpf(this.form.identity.value)) return Promise.resolve();

      if (this.cpfLookup && this.lookedUpCpf === cpfNumbers) return this.cpfLookup;

      this.lookedUpCpf = cpfNumbers;
      this.cpfLookup = this.fetchByCpf(cpfNumbers);
      return this.cpfLookup;
    },
    async fetchByCpf(cpfNumbers) {
      this.loadingCpf = true;
      this.cpfAlreadyRegistered = false;
      this.existingRole = null;
      this.foundProfessionalId = null;
      this.hasLogin = false;

      let branchRoles = [];

      await this.$httpX
        .get(`/entities/cpf:${cpfNumbers}`)
        .then(({ data: entity }) => {
          if (cpfNumbers !== this.lookedUpCpf) return null;
          branchRoles = entity.branchRoles || [];
          this.hasLogin = !!entity.hasLogin
            || !!(entity.user && entity.user.usernames && entity.user.usernames.length);
          return this.$httpX.get(`/professionals/${entity.id}`);
        })
        .then((response) => {
          if (!response || cpfNumbers !== this.lookedUpCpf) return;
          const { data } = response;
          const roles = branchRoles;
          if (roles.includes('professional')) {
            this.existingRole = 'professional';
          } else if (roles.includes('collaborator')) {
            this.existingRole = 'collaborator';
          } else {
            return;
          }

          this.foundProfessionalId = data.id;
          this.cpfAlreadyRegistered = true;
          this.form.name = data.name || '';
          this.form.birthDate = data.birthDate ? moment(data.birthDate).format('DD/MM/YYYY') : '';
          this.form.gender = data.gender || '';

          const councilList = data.professional && data.professional.councils;
          if (councilList && councilList.length > 0) {
            this.form.professional.council.name = councilList[0].name || '';
            this.form.professional.council.state = councilList[0].state || '';
            this.form.professional.council.record = councilList[0].record || '';
          }

          const specialties = data.professional && data.professional.specialties;
          if (specialties && specialties.length > 0) {
            this.form.professional.specialties = specialties
              .map(s => ({ code: s.code, name: s.name }));
          }
        })
        .catch(() => {
          if (cpfNumbers === this.lookedUpCpf) this.cpfLookup = null;
        })
        .finally(() => {
          if (cpfNumbers === this.lookedUpCpf) this.loadingCpf = false;
        });
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      this.saving = true;

      await this.lookupByCpf();

      const payload = {
        type: 'person',
        name: this.form.name,
        birthDate: moment(this.form.birthDate, 'DD/MM/YYYY').format('YYYY-MM-DD'),
        gender: this.form.gender,
        identity: {
          type: 'cpf',
          value: this.form.identity.value.replace(/\D/g, ''),
        },
        professional: {
          councils: [this.form.professional.council],
          specialties: this.form.professional.specialties,
        },
      };

      if (!this.foundProfessionalId) {
        payload.professional.active = true;
        payload.professional.permissionGroup = DEFAULT_PERMISSION_GROUP_ID;
        payload.professional.password = `${payload.identity.value}@`;
      }

      let request;
      if (this.existingRole === 'professional') {
        request = Promise.resolve({ data: { id: this.foundProfessionalId } });
      } else if (this.existingRole === 'collaborator') {
        request = this.$httpX.put(`/professionals/${this.foundProfessionalId}`, payload);
      } else {
        request = this.$httpX.post('/professionals', payload);
      }

      const messages = {
        professional: 'Profissional adicionado ao horário',
        collaborator: 'Colaborador adicionado como profissional. Senha e permissões mantidas.',
      };

      await request
        .then(({ data }) => {
          const id = this.foundProfessionalId || data.id;
          const { council } = this.form.professional;
          const newProfessionalMessage = this.hasLogin
            ? 'Profissional salvo. A pessoa já tinha acesso ao sistema e continua com a mesma senha.'
            : 'Profissional salvo. Acesso criado com o CPF e senha CPF seguido de @';
          this.$toast.show(
            messages[this.existingRole] || newProfessionalMessage,
            { timeout: 6000 },
          );
          this.$emit('saved', this.form.professional.specialties.map(specialty => ({
            id,
            name: this.form.name,
            council: { ...council },
            specialty: { ...specialty },
            isNew: !this.foundProfessionalId,
          })));
        })
        .catch((e) => {
          const message = e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : 'Erro ao salvar profissional';
          this.$toast.show(message, { type: 'error' });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
